import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Close, MarkEmailRead } from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import useLogger from 'hooks/useLogger';
import uiSlice from 'store/modules/ui';
import {
  ORG_CLIENT_AVAILABLE_PLANS,
  MY_ORGANIZATION_PLAN_REQUESTS,
  REQUEST_ORGANIZATION_PLAN
} from 'api/graphql/orgClient';

const MAX_SEATS = 5000;
const MAX_NOTE = 1000;

// Constante de módulo: `activePlanIds` es dependencia del useMemo que filtra los planes, y un
// `[]` en la firma se recrearía en cada render.
const DEFAULT_ACTIVE_PLAN_IDS = [];

function FieldLabel({ htmlFor, children }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      variant="caption"
      sx={{ display: 'block', mb: 0.75, fontWeight: 700, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

FieldLabel.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

/**
 * Solicitud de un plan nuevo para la empresa. No habilita nada por sí sola: deja el pedido
 * en estado pendiente y dispara el aviso (correo y notificación) a los administradores de
 * OTRIX, que son quienes negocian y activan el plan.
 */
function RequestPlanModal({ open, onClose, activePlanIds = DEFAULT_ACTIVE_PLAN_IDS }) {
  const { t } = useTranslation(['orgClient', 'buttons']);
  const dispatch = useDispatch();
  const { logError } = useLogger();

  const [planId, setPlanId] = useState('');
  const [seats, setSeats] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});

  const { data, loading } = useQuery(ORG_CLIENT_AVAILABLE_PLANS, {
    skip: !open,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    onError: (queryError) => logError('RequestPlanModal', queryError)
  });
  const [requestPlan, { loading: sending }] = useMutation(REQUEST_ORGANIZATION_PLAN, {
    refetchQueries: [{ query: MY_ORGANIZATION_PLAN_REQUESTS }]
  });

  // Los planes ya habilitados no se pueden volver a pedir: se sacan del selector para que la
  // solicitud no llegue duplicada al administrador.
  const plans = useMemo(() => {
    const all = data?.plans?.data || [];
    return all.filter((plan) => !activePlanIds.includes(String(plan.id)));
  }, [data, activePlanIds]);

  const reset = () => {
    setPlanId('');
    setSeats('');
    setNote('');
    setErrors({});
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const validate = () => {
    const nextErrors = {};
    if (!planId) {
      nextErrors.planId = t('plans.errors.planRequired', { defaultValue: 'Elige el plan que quieres habilitar.' });
    }
    const seatsNumber = Number(seats);
    if (seats !== '' && (!Number.isInteger(seatsNumber) || seatsNumber < 1 || seatsNumber > MAX_SEATS)) {
      nextErrors.seats = t('plans.errors.seatsRange', {
        max: MAX_SEATS,
        defaultValue: 'Escribe un número entre 1 y {{max}}.'
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await requestPlan({
        variables: {
          plan_id: planId,
          seats: seats === '' ? null : Number(seats),
          note: note.trim() || null
        }
      });
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: 'org-plan-request-success',
            variant: 'success',
            message: t('plans.requestSent', {
              defaultValue: 'Solicitud enviada. El equipo de OTRIX ya fue notificado.'
            })
          })
        )
      );
      reset();
      onClose();
    } catch (mutationError) {
      logError('RequestPlanModal.submit', mutationError);
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: 'org-plan-request-error',
            variant: 'error',
            message: t('plans.requestError', {
              defaultValue: 'No pudimos enviar tu solicitud. Intenta de nuevo en un momento.'
            })
          })
        )
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <DialogTitle sx={{ pt: 3.5, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {t('plans.requestTitle', { defaultValue: 'Solicitar otro plan' })}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label={t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1 }}>
        <Stack direction="row" spacing={1.25} sx={{ mb: 2.5, color: 'text.secondary' }}>
          <MarkEmailRead sx={{ fontSize: 18, color: 'gold.main', flex: 'none', mt: 0.25 }} />
          <Typography variant="caption">
            {t('plans.requestHint', {
              defaultValue:
                'Tu solicitud llega por correo y notificación a los administradores de OTRIX. Ellos la revisan y te contactan para habilitar el plan.'
            })}
          </Typography>
        </Stack>

        <Box sx={{ mb: 2.5 }}>
          <FieldLabel htmlFor="org-plan-select">
            {t('plans.fieldPlan', { defaultValue: 'Plan que quieres habilitar' })}
          </FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            id="org-plan-select"
            value={planId}
            disabled={loading || plans.length === 0}
            onChange={(event) => setPlanId(event.target.value)}
            error={Boolean(errors.planId)}
            helperText={
              errors.planId ||
              (plans.length === 0 && !loading
                ? t('plans.noPlansLeft', { defaultValue: 'Ya tienes habilitados todos los planes disponibles.' })
                : ' ')
            }>
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <FieldLabel htmlFor="org-plan-seats">
            {t('plans.fieldSeats', { defaultValue: 'Cupos que necesitas (opcional)' })}
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            id="org-plan-seats"
            type="number"
            value={seats}
            onChange={(event) => setSeats(event.target.value)}
            inputProps={{ min: 1, max: MAX_SEATS }}
            error={Boolean(errors.seats)}
            helperText={errors.seats || ' '}
          />
        </Box>

        <Box>
          <FieldLabel htmlFor="org-plan-note">
            {t('plans.fieldNote', { defaultValue: 'Cuéntanos qué necesitas (opcional)' })}
          </FieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            id="org-plan-note"
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, MAX_NOTE))}
            helperText={`${note.length}/${MAX_NOTE}`}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            sx={{
              py: 1.2,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              color: 'text.primary',
              borderColor: 'hairline.strong'
            }}>
            {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
          </Button>
          <Button
            fullWidth
            onClick={handleSubmit}
            disabled={sending || plans.length === 0}
            sx={{ ...premiumButtonSx, py: 1.2, fontSize: '0.95rem' }}>
            {t('plans.requestSend', { defaultValue: 'Enviar solicitud' })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

RequestPlanModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Ids (string) de los planes ya habilitados: no se ofrecen de nuevo. */
  activePlanIds: PropTypes.arrayOf(PropTypes.string)
};

export default RequestPlanModal;
